/**
 * MealCard component
 * Displays a single meal with its tags in a card format
 * Mobile-first responsive design with touch-friendly targets
 */

import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { Meal } from '@/types/api';
import { capitalize } from '@/lib/utils/text';

interface MealCardProps {
  meal: Meal;
}

/**
 * MealCard component
 *
 * Features:
 * - Displays meal name
 * - Shows non-hidden tags as badges
 * - Clickable to navigate to meal detail
 * - Responsive card design with proper spacing
 * - Touch-friendly (min 44px height)
 *
 * @example
 * ```tsx
 * <MealCard meal={meal} />
 * ```
 */
export function MealCard({ meal }: MealCardProps) {
  const { t } = useTranslation();

  // Filter out hidden tags
  const visibleTags = meal.tags.filter((tag) => !tag.isHidden);

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 p-4 sm:p-5 h-full min-h-[120px] flex flex-col">
      <Link
        to="/meals/$mealId"
        params={{ mealId: meal.id.toString() }}
        className="flex flex-col flex-grow focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded"
        aria-label={t('meals.viewDetails', { name: meal.name })}
      >
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 line-clamp-2">
          {capitalize(meal.name)}
        </h3>

        {/* Tags */}
        {visibleTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-auto">
            {visibleTags.map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center px-2 py-1 rounded text-xs sm:text-sm font-medium bg-primary-100 text-primary-800"
                aria-label={`Tag: ${tag.tag}`}
              >
                {capitalize(tag.tag)}
              </span>
            ))}
          </div>
        )}

        {/* Empty state for meals without visible tags */}
        {visibleTags.length === 0 && (
          <p className="text-sm text-gray-400 mt-auto">{t('meals.noTags')}</p>
        )}
      </Link>
    </div>
  );
}
